import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  DollarSign, FileText, Send, AlertTriangle, CheckCircle,
  XCircle, Clock, Plus, Search, Filter, Sparkles
} from 'lucide-react';
import DenialPredictionDialog from '../../components/DenialPredictionDialog';

export default function ClaimsPage() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAppeal, setShowAppeal] = useState(null);
  const [appealReason, setAppealReason] = useState('');
  const [predictionClaim, setPredictionClaim] = useState(null);

  useEffect(() => {
    loadClaims();
  }, [statusFilter]);

  const loadClaims = async () => {
    setLoading(true);
    try {
      const response = await api.get('/claims', { params: { status: statusFilter === 'all' ? undefined : statusFilter } });
      setClaims(response.data.claims || []);
    } catch (error) {
      console.error('Failed to load claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitClaim = async (claimId) => {
    try {
      await api.post(`/claims/${claimId}/submit`);
      toast.success('Claim submitted');
      loadClaims();
    } catch (error) {
      toast.error('Failed to submit claim');
    }
  };

  const handleAppeal = async () => {
    if (!appealReason.trim() || !showAppeal) return;

    try {
      await api.post(`/claims/${showAppeal}/appeal`, null, { params: { appeal_reason: appealReason } });
      toast.success('Appeal submitted');
      setShowAppeal(null);
      setAppealReason('');
      loadClaims();
    } catch (error) {
      toast.error('Failed to submit appeal');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
      case 'paid': return <CheckCircle className="w-4 h-4 text-[#166534]" />;
      case 'denied': return <XCircle className="w-4 h-4 text-[#991B1B]" />;
      case 'submitted':
      case 'pending': return <Clock className="w-4 h-4 text-[#92400E]" />;
      case 'appealed': return <AlertTriangle className="w-4 h-4 text-[#1A56DB]" />;
      default: return <FileText className="w-4 h-4 text-[#64748B]" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
      case 'paid': return 'bg-[#166534]/10 text-[#166534]';
      case 'denied': return 'bg-[#991B1B]/10 text-[#991B1B]';
      case 'submitted':
      case 'pending': return 'bg-[#92400E]/10 text-[#92400E]';
      case 'appealed': return 'bg-[#1A56DB]/10 text-[#1A56DB]';
      default: return 'bg-[#64748B]/10 text-[#64748B]';
    }
  };

  const filteredClaims = claims.filter(claim =>
    claim.patient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    claim.claim_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats
  const stats = {
    total: claims.length,
    pending: claims.filter(c => ['draft', 'submitted', 'pending'].includes(c.status)).length,
    denied: claims.filter(c => c.status === 'denied').length,
    totalAmount: claims.reduce((sum, c) => sum + (c.total_amount || 0), 0)
  };

  return (
    <div className="space-y-6" data-testid="claims-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Claims Management
          </h1>
          <p className="text-[#64748B] mt-1">
            Submit and track insurance claims
          </p>
        </div>
        <Link to="/claims/new">
          <Button className="careiq-btn-primary flex items-center gap-2" data-testid="new-claim-btn">
            <Plus className="w-4 h-4" />
            New Claim
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="careiq-metric-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0E7490]/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#0E7490]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{stats.total}</div>
                <div className="text-xs text-[#64748B]">Total Claims</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="careiq-metric-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#92400E]/10 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#92400E]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{stats.pending}</div>
                <div className="text-xs text-[#64748B]">Pending</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="careiq-metric-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#991B1B]/10 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-[#991B1B]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{stats.denied}</div>
                <div className="text-xs text-[#64748B]">Denied</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="careiq-metric-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#166534]/10 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#166534]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">${stats.totalAmount.toLocaleString()}</div>
                <div className="text-xs text-[#64748B]">Total Billed</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="careiq-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input
                placeholder="Search by patient or claim number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="careiq-input pl-10"
                data-testid="claim-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="in_review">In Review</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="denied">Denied</SelectItem>
                <SelectItem value="appealed">Appealed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Claims Table */}
      <Card className="careiq-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredClaims.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="table-header text-left">Claim #</th>
                    <th className="table-header text-left">Patient</th>
                    <th className="table-header text-left">Service Date</th>
                    <th className="table-header text-left">Amount</th>
                    <th className="table-header text-left">Insurance</th>
                    <th className="table-header text-left">Status</th>
                    <th className="table-header text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClaims.map((claim) => (
                    <tr key={claim.id} className="table-row">
                      <td className="table-cell font-mono text-sm">{claim.claim_number}</td>
                      <td className="table-cell font-medium">{claim.patient_name}</td>
                      <td className="table-cell text-[#64748B]">{claim.service_date}</td>
                      <td className="table-cell font-medium">${claim.total_amount?.toLocaleString()}</td>
                      <td className="table-cell text-[#64748B]">{claim.insurance_provider || '-'}</td>
                      <td className="table-cell">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(claim.status)}`}>
                          {getStatusIcon(claim.status)}
                          {claim.status}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          {(claim.status === 'draft' || claim.status === 'submitted') && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-[#0E7490] border-[#0E7490]/30 hover:bg-[#0E7490]/5"
                              onClick={() => setPredictionClaim(claim)}
                              data-testid={`predict-denial-btn-${claim.id}`}
                            >
                              <Sparkles className="w-3 h-3 mr-1" />
                              AI Check
                            </Button>
                          )}
                          {claim.status === 'draft' && (
                            <Button 
                              size="sm" 
                              className="careiq-btn-secondary"
                              onClick={() => handleSubmitClaim(claim.id)}
                            >
                              <Send className="w-3 h-3 mr-1" />
                              Submit
                            </Button>
                          )}
                          {claim.status === 'denied' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-[#1A56DB]"
                              onClick={() => setShowAppeal(claim.id)}
                            >
                              Appeal
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-[#0E7490]">
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-[#64748B]">
              <FileText className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" />
              <p>No claims found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Appeal Dialog */}
      <Dialog open={!!showAppeal} onOpenChange={() => setShowAppeal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Appeal Denied Claim</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="careiq-label">Reason for Appeal</Label>
              <Textarea
                placeholder="Explain why this claim should be reconsidered..."
                value={appealReason}
                onChange={(e) => setAppealReason(e.target.value)}
                className="careiq-input min-h-[120px]"
                data-testid="appeal-reason"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAppeal(null)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleAppeal} data-testid="submit-appeal-btn">
                Submit Appeal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Denial Prediction Dialog */}
      <DenialPredictionDialog
        open={!!predictionClaim}
        onClose={() => setPredictionClaim(null)}
        claim={predictionClaim}
      />
    </div>
  );
}
